/**
 * IMPORTANT: This function is duplicated in the backend at apps/api/src/utils/shortenUrl.ts
 * Any changes to the URL shortening logic must be made in both places to maintain consistency.
 *
 * TODO: Consider moving this to a shared package to avoid duplication and ensure consistency.
 */

const BASE62 = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Converts a full inspection URL containing a UUID into a shortened URL.
 *
 * @param {string} url - The full inspection URL (e.g., "/inspection/{UUID}").
 *                       The UUID should be in standard format with hyphens.
 * @returns {string} A shortened URL in the format "/s/{shortId}".
 *                   If the input does not match the expected format, the original URL is returned.
 */
export const shortenInspectionUrl = (url: string): string => {
  try {
    // Extract just the path portion of the URL
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // Extract the inspection ID
    const match = path.match(/\/inspection\/([^\/]+)/);
    if (!match) return path;

    const inspectionId = match[1];

    // Convert the full UUID to a shorter string
    // First, remove any hyphens
    const cleanId = inspectionId.replace(/-/g, '');

    // Convert the UUID to a number using BigInt to handle large numbers
    const uuidNum = BigInt('0x' + cleanId);

    // Convert to base62
    let result = '';
    let n = uuidNum;
    const base = BigInt(62);
    do {
      const remainder = Number(n % base);
      result = BASE62[remainder] + result;
      n = n / base;
    } while (n > 0);

    // Pad to ensure consistent length (UUID in base62 should be 22 chars)
    result = result.padStart(22, BASE62[0]);

    // Add prefix to indicate it's a shortened URL
    return `/s/${result}`;
  } catch (err) {
    console.error('Error shortening URL:', err);
    return url;
  }
};

// Convert a shortened URL back to the original inspection URL
export const expandInspectionUrl = (shortUrl: string): string => {
  try {
    // Extract the short ID
    const match = shortUrl.match(/\/s\/([^\/]+)/);
    if (!match) return shortUrl;

    const shortId = match[1];

    // Convert from base62 back to a number
    let num = BigInt(0);
    const base = BigInt(62);
    for (let i = 0; i < shortId.length; i++) {
      num = num * base + BigInt(BASE62.indexOf(shortId[i]));
    }

    // Convert to hex and pad to 32 characters (UUID length)
    let hex = num.toString(16).padStart(32, '0');

    // Format as UUID
    const uuid = [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-');

    // Always return a result URL
    return `/inspection/${uuid}/result`;
  } catch (err) {
    console.error('Error expanding URL:', err);
    return shortUrl;
  }
};
